import * as XLSX from 'xlsx';
import { ExcelPropertyInfo, ExcelViewerErrorData } from 'types';
import { ExcelKeyValuePairs } from 'components/Contexts/ExcelKeyValuePairs';
import { ClientValidationErrorType, ClientValidationError } from 'errors';
import { commonRegex } from 'utils';

type ExcelUploadValue = string | number;

const isoDatePattern = /^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})\s*T\d{2}:\d{2}:\d{2}\.?\d*Z?$/i;

const customerIdentifierHeaders = new Set([
    "CUSTOMERID",
    "CUSTOMERNUMBER",
    "CUSTOMERSHIPTO",
    "CUSTOMERSHIPTONUMBER",
]);

const standardizeHeader = (value: unknown) => (value ?? "").toString().replace(/\s/g, "").toUpperCase();

const isCustomerIdentifierHeader = (header: unknown) => customerIdentifierHeaders.has(standardizeHeader(header));

export const useExcelValidationHelpers = () => {
    const regex = { hasSpaces: commonRegex.hasSpaces,
                    startsWithVowel: /^[aeiou]/i };
    
    //Logs error for omitted required properties
    const checkRequiredField = <THeaders>(columnHeaderEnum: THeaders, props: ExcelPropertyInfo) => {
        const { propName, value, dataRow, log, errorKey } = props,
                propNameKey = propName as keyof THeaders,
                propColumnName = `column "${columnHeaderEnum[propNameKey]}"`; 
        if(!value && (typeof log !== "undefined")) {
            const errText = `Empty value for ${propColumnName}, this field is required.`;
            log.push(new ExcelViewerErrorData(dataRow, errorKey ?? propName ?? "", [errText]));
            return false;
        }
        return true;
    };


    //Throws an error to abort validation if Excel data appears to have incorrect column headers, or if data is empty
    const checkExcelHeaders = <TemplateModel>(excel: TemplateModel[] | null, 
                                              skipNumHeaders: number, 
                                              additionalChecks?: () => void) => 
    {
        if (!excel || excel.length === 0) {
            throw new ClientValidationError(ClientValidationErrorType.NoData, 
                                            "No Excel data to validate!", 
                                            null);
        }
        if (skipNumHeaders < 0) {
            throw new ClientValidationError(ClientValidationErrorType.BadExcelHeaders, 
                                            "Validation service: skipNumHeaders must be a positive number.", 
                                            null);
        }
        if (excel.length <= skipNumHeaders) {
            throw new ClientValidationError(ClientValidationErrorType.NoData, 
                                            "Excel data appears to contain no data rows, only headers. ",
                                            null);
        }
        if (additionalChecks) additionalChecks();
    };

    //Parses an int from the value of propinfo, logs an error and returns 0 on failure.
    const tryParseInt = <THeaders>(columnHeaderEnum: THeaders, propInfo: ExcelPropertyInfo) => {
        const { propName, value, dataRow, errorKey, log } = propInfo;
        const parsed = parseInt(value ?? ""),
              success = !(Number.isNaN(parsed)),
              propNameKey = propName as keyof THeaders,
              propColumnName = `column "${columnHeaderEnum[propNameKey]}"`; 
        //console.log("tryParseInt " + propName + ": " + value)
        if(!success && (typeof log !== "undefined")) {
            const errText = `Unable to parse integer "${value}"` + 
                            `${columnHeaderEnum[propNameKey] ? ` in ${propColumnName}` : ""}.`;
            log.push(new ExcelViewerErrorData(dataRow, errorKey ?? propName ?? "", [errText]));
        }
        return success ? parsed : 0;
    };

    //Strips spaces and lower case letters out of Excel template property names
    const standardizePropertyNames = <TExcelTemplate>(rawExcel: any[]): TExcelTemplate[] => {
        const matchPattern: RegExp = regex.hasSpaces,
              newExcelArray: TExcelTemplate[] = [];
        for(const row of rawExcel) {
            const removeSpaces = Object.entries(row)
                                       .map(([key, value]) => {
                                        //A quirk with JavaScript's regex engine is that  
                                        //we have to reset lastIndex manually here
                                            matchPattern.lastIndex = 0;
                                            return [key.replace(matchPattern, '').toUpperCase(), value];
                                       }),
                  standardPropNames = Object.fromEntries(removeSpaces);
            newExcelArray.push(standardPropNames as TExcelTemplate);
        }
        return newExcelArray;
    };

    const trimValuesLeadingAndTrailingWhitespace = <TExcelTemplate>(rawExcel: any[]): TExcelTemplate[] => {
        const result = rawExcel.map(row => {
            const kvps = Object.entries(row)
                               .map(([key, value]) => {
                                //This check is likely redundant b/c we import everything as strings, but just in case
                                   if (value && (typeof value === "string" || value instanceof String)) {
                                       return [key, value.trim()];
                                   }
                                   else return [key, value];
                               });
            return Object.fromEntries(kvps) as TExcelTemplate
        });
        return result;
    }

    //For error message, contextual "a" or "an"
    const grammarHelper = (str: string) => 
        regex.startsWithVowel.test(str.trim()) ? `an ${str}` : `a ${str}`;

    const preserveCustomerIdentifierText = (worksheet: XLSX.WorkSheet): XLSX.WorkSheet => {
        const range = XLSX.utils.decode_range(worksheet["!ref"] ?? "A1:A1");

        for (let col = range.s.c; col <= range.e.c; col++) {
            const headerAddress = XLSX.utils.encode_cell({ r: range.s.r, c: col });
            const header = worksheet[headerAddress]?.v;

            if (!isCustomerIdentifierHeader(header)) continue;

            for (let row = range.s.r + 1; row <= range.e.r; row++) {
                const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                const cell = worksheet[cellAddress];

                if (cell?.w) {
                    const value = cell.w.toString().trim();
                    cell.v = value;
                    cell.w = value;
                    cell.t = "s";
                }
            }
        }

        return worksheet;
    };

    const normalizeExcelUploadRows = (rawData: ExcelKeyValuePairs[]) => {
        return rawData
            .filter(row => Object.values(row).some(value => value !== ""))
            .map(row => {
                const kvps = Object.entries(row)
                    .map(([key, rawVal]) => {
                        const stringValue = (rawVal ?? "").toString().trim();
                        let value: ExcelUploadValue = stringValue;

                        if (stringValue && isoDatePattern.test(stringValue)) {
                            const extracted = isoDatePattern.exec(stringValue)?.groups;
                            const month = extracted?.["month"];
                            const day = extracted?.["day"];
                            const year = extracted?.["year"];
                            value = `${month}/${day}/${year}`;
                        } else if (typeof rawVal === "number") {
                            value = Math.round((rawVal + Number.EPSILON) * 10000) / 10000;
                        }

                        return [key, value];
                    });
                return Object.fromEntries(kvps);
            });
    };

    return {
        checkRequiredField,
        checkExcelHeaders,
        tryParseInt,
        standardizePropertyNames,
        trimValuesLeadingAndTrailingWhitespace,
        grammarHelper,
        preserveCustomerIdentifierText,
        normalizeExcelUploadRows
    };
}
